import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Scale, ArrowRight } from 'lucide-react';
import { useLang } from '../../i18n/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const L = {
  en: {
    label: 'Compared Honestly',
    h2: 'How DAAKYI stacks up against the global clouds',
    sub: 'Fair, structured comparisons — sovereignty, latency from African cities, local billing and support. Including where they win.',
    all: 'See all comparisons', read: 'Read'
  },
  fr: {
    label: 'Comparé honnêtement',
    h2: 'DAAKYI face aux clouds mondiaux',
    sub: 'Des comparaisons équitables et structurées — souveraineté, latence depuis les villes africaines, facturation et support locaux. Y compris là où ils gagnent.',
    all: 'Voir toutes les comparaisons', read: 'Lire'
  }
};

export const CompareTeaserSection = () => {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/compare-directory?lang=${lang}`)
      .then(r => r.json()).then(d => setItems((d.items || []).slice(0, 4))).catch(() => setItems([]));
  }, [lang]);

  if (items.length === 0) return null;

  return (
    <section data-testid="compare-teaser-section" data-no-translate className="bg-[#F8FAFC] py-20 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <p className="inline-flex items-center gap-2 text-[#A8631A] text-xs font-bold tracking-[2.5px] uppercase mb-3">
              <Scale className="w-4 h-4" /> {t.label}
            </p>
            <h2 className="text-[#1A1240] text-base md:text-lg font-extrabold tracking-tight max-w-[560px] !text-2xl md:!text-3xl">{t.h2}</h2>
            <p className="text-[#64748B] text-base mt-3 max-w-[560px]">{t.sub}</p>
          </div>
          <Link to="/compare" data-testid="compare-teaser-all"
            className="inline-flex items-center gap-1.5 text-[#A8631A] hover:text-[#DD8226] text-sm font-bold transition-colors group">
            {t.all} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(c => (
            <Link key={c.slug} to={`/compare/${c.slug}`} data-testid={`compare-teaser-${c.slug}`}
              className="group bg-white border border-[#E2E8F0] rounded-xl overflow-hidden hover:border-[#F29B3D]/60 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(18,8,54,0.08)] transition-all">
              {c.has_cover && (
                <img src={`${API}/api/compare-directory/${c.slug}/cover`} alt="" loading="lazy"
                  className="w-full aspect-[16/8] object-cover" />
              )}
              <div className="p-4">
                <p className="text-[#F29B3D] text-[10px] font-extrabold tracking-[1.5px] uppercase mb-1">vs {c.competitor}</p>
                <p className="text-[#1A1240] text-sm font-bold leading-snug line-clamp-2">{c.title}</p>
                <p className="text-[#A8631A] text-xs font-bold mt-2.5 group-hover:text-[#DD8226]">{t.read} →</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
